import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { assignmentAPI } from '../services/api';
import { Skeleton } from '@mui/material';

const MyCodingAssignments = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignmentCode, setAssignmentCode] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [checkingCode, setCheckingCode] = useState(false);

  // Pagination, search, and sort state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState(''); // For debounced search
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const itemsPerPage = 10;

  const developerLevelLabels = {
    internDeveloper: { label: 'Intern Developer', color: '#48bb78' },
    juniorDeveloper: { label: 'Junior Developer', color: '#667eea' },
    midLevelDeveloper: { label: 'Mid-Level Developer', color: '#fbbf24' },
    seniorDeveloper: { label: 'Senior Developer', color: '#f97316' },
    leadDeveloper: { label: 'Lead Developer', color: '#ef4444' }
  };

  // Helper function to format time spent
  const formatTimeSpent = (seconds) => {
    if (!seconds || seconds === 0) return null;
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return `${secs}s`;
    }
  };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(1);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchMyAssignments();
  }, [currentPage, searchQuery, sortBy, sortOrder]);

  const fetchMyAssignments = async () => {
    setLoading(true);
    try {
      const response = await assignmentAPI.getMyAssignments({
        page: currentPage,
        limit: itemsPerPage,
        search: searchQuery,
        sortBy,
        sortOrder
      });
      setSubmissions(response.data.submissions || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
      setTotalItems(response.data.pagination?.totalItems || 0);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading assignments');
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchInput(value);
    // searchQuery will be updated by the debounce effect
  };

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      // Toggle sort order if clicking same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
    setCurrentPage(1); // Reset to first page on sort change
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAccessAssignment = async () => {
    if (!assignmentCode.trim() || assignmentCode.length !== 6) {
      return;
    }

    setCheckingCode(true);
    try {
      // Validate the assignment code exists
      await assignmentAPI.getAssignmentByCode(assignmentCode.trim());
      // If successful, navigate to the assignment
      navigate(`/coding-assignment/${assignmentCode.trim()}`);
    } catch (err) {
      // Show error modal if code is invalid
      setErrorMessage(err.response?.data?.message || 'Invalid assignment code. Please check and try again.');
      setShowErrorModal(true);
      setCheckingCode(false);
    }
  };

  return (
    <div className="container">
      <div className="card">
        <div style={{ marginBottom: '24px' }}>
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/user')}
            style={{ fontSize: '12px', marginBottom: '16px' }}
          >
            ← Back to Dashboard
          </button>
          <h2 style={{ color: '#667eea', fontSize: '22px', fontWeight: '700', marginBottom: '6px' }}>
            💻 My Coding Assignments
          </h2>
          <p style={{ color: '#718096', fontSize: '13px' }}>
            Practice coding with step-by-step assignments and get instant AI feedback
          </p>
        </div>

        {/* Access Assignment by Code */}
        <div style={{
          backgroundColor: '#f7fafc',
          border: '1.5px solid #e2e8f0',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <h3 style={{ color: '#2d3748', fontSize: '15px', fontWeight: '600', marginBottom: '8px' }}>
            🔑 Access New Assignment
          </h3>
          <p style={{ color: '#718096', fontSize: '13px', marginBottom: '16px' }}>
            Enter the assignment code provided by your instructor
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              type="text"
              value={assignmentCode}
              onChange={(e) => setAssignmentCode(e.target.value.toUpperCase())}
              placeholder="Enter 6-character code"
              maxLength={6}
              style={{
                flex: 1,
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                textTransform: 'uppercase',
                fontFamily: 'monospace',
                letterSpacing: '2px',
                backgroundColor: 'white'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleAccessAssignment();
              }}
            />
            <button
              onClick={handleAccessAssignment}
              disabled={assignmentCode.length !== 6 || checkingCode}
              className="btn btn-primary"
              style={{
                fontSize: '13px',
                opacity: (assignmentCode.length === 6 && !checkingCode) ? 1 : 0.5,
                cursor: (assignmentCode.length === 6 && !checkingCode) ? 'pointer' : 'not-allowed'
              }}
            >
              {checkingCode ? 'Checking...' : 'Start Assignment →'}
            </button>
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fef2f2',
            color: '#ef4444',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #fecaca',
            fontSize: '13px'
          }}>
            {error}
          </div>
        )}

        {/* Search and Sort Controls */}
        {!loading && totalItems > 0 && (
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '20px',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            {/* Search Bar */}
            <div style={{ flex: 1, minWidth: '250px' }}>
              <input
                type="text"
                placeholder="🔍 Search assignments..."
                value={searchInput}
                onChange={(e) => handleSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>

            {/* Sort Dropdown */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#718096', fontWeight: '500' }}>Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  color: '#2d3748',
                  fontWeight: '500'
                }}
              >
                <option value="updatedAt">Last Updated</option>
                <option value="createdAt">Date Started</option>
                <option value="title">Title</option>
                <option value="progress">Progress</option>
                <option value="complexity">Complexity</option>
              </select>
              
              {/* Sort Order Toggle */}
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                style={{
                  padding: '8px 12px',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '16px',
                  transition: 'all 0.2s'
                }}
                title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>

            {/* Results Count */}
            <div style={{ 
              fontSize: '13px', 
              color: '#718096',
              padding: '8px 12px',
              backgroundColor: '#f7fafc',
              borderRadius: '6px',
              fontWeight: '500'
            }}>
              {totalItems} assignment{totalItems !== 1 ? 's' : ''}
            </div>
          </div>
        )}

        {/* My Assignments List */}
        {!loading && submissions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', background: '#f7fafc', borderRadius: '12px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              {searchQuery ? '🔍' : '📝'}
            </div>
            <p style={{ color: '#718096', fontSize: '15px', marginBottom: '8px' }}>
              {searchQuery ? 'No assignments found' : 'No assignments started yet'}
            </p>
            <p style={{ color: '#718096', fontSize: '13px', marginBottom: '16px' }}>
              {searchQuery 
                ? `No assignments match "${searchQuery}". Try a different search term.`
                : 'Enter an assignment code above to get started!'}
            </p>
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSearchInput('');
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Clear Search
              </button>
            )}
          </div>
        ) : loading ? (
          <div style={{ display: 'grid', gap: '12px' }}>
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '18px',
                  background: '#fafafa'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    {/* Title */}
                    <Skeleton variant="text" width="60%" height={24} sx={{ marginBottom: '8px' }} />
                    
                    {/* Description */}
                    <Skeleton variant="text" width="90%" height={18} sx={{ marginBottom: '4px' }} />
                    <Skeleton variant="text" width="75%" height={18} sx={{ marginBottom: '12px' }} />
                    
                    {/* Badges */}
                    <div style={{ marginBottom: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      <Skeleton variant="rectangular" width={80} height={20} sx={{ borderRadius: '10px' }} />
                      <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: '10px' }} />
                      <Skeleton variant="rectangular" width={120} height={20} sx={{ borderRadius: '10px' }} />
                      <Skeleton variant="rectangular" width={90} height={20} sx={{ borderRadius: '10px' }} />
                    </div>
                    
                    {/* Language line */}
                    <Skeleton variant="text" width="40%" height={18} sx={{ marginBottom: '12px' }} />
                  </div>
                  
                  {/* Status badge */}
                  <Skeleton variant="rectangular" width={90} height={28} sx={{ borderRadius: '6px' }} />
                </div>
                
                {/* Progress bar section */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <Skeleton variant="text" width={80} height={16} />
                    <Skeleton variant="text" width={40} height={16} />
                  </div>
                  <Skeleton variant="rectangular" width="100%" height={8} sx={{ borderRadius: '4px' }} />
                </div>
                
                {/* Stats grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '12px' }}>
                  <div style={{ background: 'white', borderRadius: '6px', border: '1px solid #e2e8f0', padding: '8px', textAlign: 'center' }}>
                    <Skeleton variant="text" width="70%" height={20} sx={{ margin: '0 auto', marginBottom: '4px' }} />
                    <Skeleton variant="text" width="90%" height={14} sx={{ margin: '0 auto' }} />
                  </div>
                  <div style={{ background: 'white', borderRadius: '6px', border: '1px solid #e2e8f0', padding: '8px', textAlign: 'center' }}>
                    <Skeleton variant="text" width="70%" height={20} sx={{ margin: '0 auto', marginBottom: '4px' }} />
                    <Skeleton variant="text" width="90%" height={14} sx={{ margin: '0 auto' }} />
                  </div>
                  <div style={{ background: 'white', borderRadius: '6px', border: '1px solid #e2e8f0', padding: '8px', textAlign: 'center' }}>
                    <Skeleton variant="text" width="70%" height={20} sx={{ margin: '0 auto', marginBottom: '4px' }} />
                    <Skeleton variant="text" width="90%" height={14} sx={{ margin: '0 auto' }} />
                  </div>
                </div>
                
                {/* Timestamp */}
                <Skeleton variant="text" width="50%" height={14} />
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {submissions.map((sub) => {
              const assignment = sub.assignment;
              if (!assignment) return null;

              const levelConfig = developerLevelLabels[assignment.developerLevel];
              const completedSteps = sub.stepSubmissions.filter(ss => ss.isComplete).length;
              
              // Get total steps from algorithmByLanguage or fallback to steps array
              let totalSteps = 0;
              if (assignment.algorithmByLanguage && Object.keys(assignment.algorithmByLanguage).length > 0) {
                const firstLang = Object.keys(assignment.algorithmByLanguage)[0];
                totalSteps = assignment.algorithmByLanguage[firstLang]?.steps?.length || 0;
              } else {
                totalSteps = assignment.steps?.length || 0;
              }
              
              // Calculate progress with 80% for steps, 20% for quizzes
              const stepsProgress = totalSteps > 0 ? (completedSteps / totalSteps) * 80 : 0;
              const quizProgress = sub.isCompleted ? 20 : 0;
              const progressPercent = Math.round(stepsProgress + quizProgress);

              return (
                <div
                  key={sub._id}
                  onClick={() => navigate(`/coding-assignment/${assignment.assignmentCode}`)}
                  style={{
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '18px',
                    background: '#fafafa',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#fafafa';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ 
                        color: '#2d3748', 
                        fontSize: '16px', 
                        fontWeight: '600',
                        marginBottom: '4px'
                      }}>
                        {assignment.title}
                      </h3>
                      {assignment.description && (
                        <p style={{ color: '#718096', fontSize: '13px', marginBottom: '8px' }}>
                          {assignment.description.length > 120 
                            ? assignment.description.substring(0, 120) + '...' 
                            : assignment.description}
                        </p>
                      )}
                      
                      {/* Tags/Badges */}
                      <div style={{ marginBottom: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <span style={{
                          padding: '2px 8px',
                          background: 'white',
                          color: '#667eea',
                          borderRadius: '10px',
                          fontSize: '10px',
                          fontWeight: '500',
                          border: '1px solid #c7d2fe'
                        }}>
                          📋 {assignment.assignmentCode}
                        </span>
                        <span style={{
                          padding: '2px 8px',
                          background: 'white',
                          color: assignment.complexity === 'Easy' ? '#48bb78' : 
                                 assignment.complexity === 'Medium' ? '#f59e0b' : '#ef4444',
                          borderRadius: '10px',
                          fontSize: '10px',
                          fontWeight: '500',
                          border: `1px solid ${assignment.complexity === 'Easy' ? '#9ae6b4' : 
                                              assignment.complexity === 'Medium' ? '#fcd34d' : '#fca5a5'}`
                        }}>
                          {assignment.complexity}
                        </span>
                        <span style={{
                          padding: '2px 8px',
                          background: levelConfig.color,
                          color: 'white',
                          borderRadius: '10px',
                          fontSize: '10px',
                          fontWeight: '500'
                        }}>
                          {levelConfig.label}
                        </span>
                        {assignment.tags && assignment.tags.length > 0 && assignment.tags.map((tag, index) => (
                          <span key={index} style={{ 
                            padding: '2px 8px',
                            background: '#f0fff4',
                            color: '#2f855a',
                            borderRadius: '10px',
                            fontSize: '10px',
                            fontWeight: '500',
                            border: '1px solid #9ae6b4'
                          }}>
                            🏷️ {tag.name || tag}
                          </span>
                        ))}
                      </div>
                      
                      <p style={{ color: '#4a5568', fontSize: '12px', marginBottom: '8px' }}>
                        💻 <strong>{assignment.languages?.join(', ').toUpperCase() || 'N/A'}</strong>
                      </p>
                    </div>
                    
                    <div style={{
                      padding: '6px 12px',
                      backgroundColor: sub.isCompleted ? '#d1fae5' : '#fef3c7',
                      color: sub.isCompleted ? '#065f46' : '#92400e',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                      marginLeft: '12px'
                    }}>
                      {sub.isCompleted ? '✓ Completed' : '⏳ In Progress'}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', color: '#718096', fontWeight: '500' }}>
                        Progress: {completedSteps}/{totalSteps} steps
                        {completedSteps === totalSteps && !sub.isCompleted && (
                          <span style={{ marginLeft: '6px', color: '#f59e0b', fontStyle: 'italic' }}>
                            • Quizzes pending
                          </span>
                        )}
                      </span>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: progressPercent === 100 ? '#48bb78' : '#667eea' }}>
                        {progressPercent}%
                      </span>
                    </div>
                    <div style={{
                      width: '100%',
                      height: '6px',
                      backgroundColor: '#e2e8f0',
                      borderRadius: '3px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${progressPercent}%`,
                        height: '100%',
                        backgroundColor: progressPercent === 100 ? '#48bb78' : '#667eea',
                        transition: 'width 0.3s ease',
                        borderRadius: '3px'
                      }} />
                    </div>
                    
                    {/* Time Spent - Show only for completed assignments */}
                    {sub.isCompleted && sub.timeSpent > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: '#718096' }}>
                          ⏱️ Time Spent:
                        </span>
                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#667eea' }}>
                          {formatTimeSpent(sub.timeSpent)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && totalPages > 1 && (
          <div style={{
            marginTop: '24px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            {/* Previous Button */}
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '8px 16px',
                border: '1.5px solid #e2e8f0',
                borderRadius: '6px',
                backgroundColor: currentPage === 1 ? '#f7fafc' : 'white',
                color: currentPage === 1 ? '#cbd5e0' : '#667eea',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
            >
              ← Previous
            </button>

            {/* Page Numbers */}
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {[...Array(totalPages)].map((_, index) => {
                const pageNum = index + 1;
                // Show first page, last page, current page, and pages around current
                const showPage = pageNum === 1 || 
                                pageNum === totalPages || 
                                Math.abs(pageNum - currentPage) <= 1;
                
                // Show ellipsis
                const showEllipsisBefore = pageNum === currentPage - 2 && currentPage > 3;
                const showEllipsisAfter = pageNum === currentPage + 2 && currentPage < totalPages - 2;

                if (showEllipsisBefore || showEllipsisAfter) {
                  return (
                    <span key={pageNum} style={{ color: '#cbd5e0', padding: '0 4px' }}>
                      ...
                    </span>
                  );
                }

                if (!showPage) return null;

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    style={{
                      padding: '8px 12px',
                      border: currentPage === pageNum ? '1.5px solid #667eea' : '1.5px solid #e2e8f0',
                      borderRadius: '6px',
                      backgroundColor: currentPage === pageNum ? '#667eea' : 'white',
                      color: currentPage === pageNum ? 'white' : '#4a5568',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: currentPage === pageNum ? '700' : '500',
                      minWidth: '36px',
                      transition: 'all 0.2s'
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            {/* Next Button */}
            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '8px 16px',
                border: '1.5px solid #e2e8f0',
                borderRadius: '6px',
                backgroundColor: currentPage === totalPages ? '#f7fafc' : 'white',
                color: currentPage === totalPages ? '#cbd5e0' : '#667eea',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
            >
              Next →
            </button>

            {/* Page Info */}
            <div style={{
              marginLeft: '12px',
              padding: '8px 12px',
              backgroundColor: '#f7fafc',
              borderRadius: '6px',
              fontSize: '12px',
              color: '#718096',
              fontWeight: '500'
            }}>
              Page {currentPage} of {totalPages}
            </div>
          </div>
        )}
      </div>

      {/* Error Modal */}
      {showErrorModal && (
        <div
          onClick={() => {
            setShowErrorModal(false);
            setAssignmentCode('');
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>❌</div>
              <h2 style={{ color: '#ef4444', fontSize: '22px', fontWeight: '700', marginBottom: '8px' }}>
                Invalid Assignment Code
              </h2>
              <p style={{ color: '#718096', fontSize: '15px', lineHeight: '1.6' }}>
                {errorMessage}
              </p>
            </div>

            <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f7fafc', borderRadius: '8px' }}>
              <p style={{ color: '#4a5568', fontSize: '13px', marginBottom: '8px', fontWeight: '600' }}>
                💡 Troubleshooting Tips:
              </p>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#718096', fontSize: '13px', lineHeight: '1.8' }}>
                <li>Check that you've entered the correct 6-character code</li>
                <li>Ensure there are no extra spaces</li>
                <li>Verify the code with your instructor</li>
                <li>Make sure the assignment hasn't expired</li>
              </ul>
            </div>

            <button
              onClick={() => {
                setShowErrorModal(false);
                setAssignmentCode('');
              }}
              className="btn btn-primary"
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '15px',
                fontWeight: '600'
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyCodingAssignments;
